import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import { useGetLesson, useGetLessons, useGetProgress } from '@onesource/services';
import { useNavigate } from '@tanstack/react-router';
import { type FC } from 'react';

import { useContentRoute } from '../hooks/use-content-route';
import { segmentForBlock } from '../utils/content-urls';
import { LessonDetailSkeleton } from './lesson-detail-skeleton';
import { LessonDetailView } from './lesson-detail-view';
import { NavBreadcrumbCurrent, NavBreadcrumbLabel, NavBreadcrumbs } from './nav-breadcrumbs';
import { StickyNavRow } from './sticky-nav-row';

/**
 * The lesson view for a single-lesson program, rendered at the program home
 * `/programs/{program}` (a single-lesson space has no modules — the program IS
 * the lesson). Content blocks open at `/programs/{program}/{type}/{item}`.
 */
export const SingleLessonView: FC = () => {
  const { programId, programSlug } = useContentRoute();
  const navigate = useNavigate();

  const { data: lessons, isLoading: isLoadingLessons } = useGetLessons(programId);
  const lessonId = lessons?.[0]?.id;
  const { data: lesson, isLoading: isLoadingLesson } = useGetLesson(lessonId ?? '', { enabled: !!lessonId });
  const { data: progress } = useGetProgress(lessonId ?? '');

  if (isLoadingLessons || (lessonId && isLoadingLesson)) {
    return <LessonDetailSkeleton />;
  }

  if (!lesson) {
    return (
      <Box p={3}>
        <Alert severity="info">
          This program has no lesson yet. An admin needs to publish the lesson from the dashboard.
        </Alert>
      </Box>
    );
  }

  const breadcrumbs = (
    <StickyNavRow backHref="/programs" backLabel="Programs">
      <NavBreadcrumbs>
        <NavBreadcrumbCurrent>
          <NavBreadcrumbLabel html={lesson.title} />
        </NavBreadcrumbCurrent>
      </NavBreadcrumbs>
    </StickyNavRow>
  );

  return (
    <LessonDetailView
      lesson={lesson}
      progressPercent={progress?.progressPercent ?? 0}
      breadcrumbs={breadcrumbs}
      onNavigateToBlock={block => {
        const segment = segmentForBlock(block);
        if (!segment || !block.urlSlug) {
          return;
        }
        navigate({ to: `/programs/${programSlug}/${segment}/${block.urlSlug}` });
      }}
      onNavigateToSubLesson={() => {
        // Sub-lessons under a single-lesson space have no dedicated slug URL;
        // stay on the program lesson view.
        navigate({ to: '/programs/$programSlug', params: { programSlug } });
      }}
    />
  );
};
